import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../utils/api';
import { useToast } from '../../components/common/Toast';
import './AuditLog.css';

const ENTITY_TYPES = [
  '', 'auth', 'user', 'time_off_request', 'pto_allocation', 'bonus_award',
  'holiday', 'module', 'tool', 'tool_category', 'department', 'blackout', 'app_settings',
];

function AuditLog() {
  const toast = useToast();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 50;

  // Expanded rows
  const [expandedRows, setExpandedRows] = useState(new Set());

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (entityType) params.set('entityType', entityType);
      if (action) params.set('action', action);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      params.set('limit', limit);
      params.set('offset', (page - 1) * limit);

      const res = await api.get(`/admin/audit-log?${params.toString()}`);
      setEntries(res.entries || []);
      setTotal(res.total || 0);
    } catch (err) {
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [entityType, action, startDate, endDate, page]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const totalPages = Math.ceil(total / limit);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="audit-log-page">
      <div className="page-header">
        <FileText size={22} strokeWidth={2} />
        <h1>Audit Log</h1>
        <span className="total-count">{total} entries</span>
      </div>

      {/* Filters */}
      <div className="audit-filters">
        <select className="filter-select" value={entityType} onChange={(e) => { setEntityType(e.target.value); setPage(1); }}>
          <option value="">All Entity Types</option>
          {ENTITY_TYPES.filter(Boolean).map(t => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <div className="search-input-wrap">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Filter by action..."
            value={action}
            onChange={(e) => { setAction(e.target.value); setPage(1); }}
          />
        </div>
        <input
          type="date"
          className="filter-date"
          value={startDate}
          onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
          placeholder="Start date"
        />
        <input
          type="date"
          className="filter-date"
          value={endDate}
          onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
          placeholder="End date"
        />
      </div>

      {/* Table */}
      <div className="audit-table-card">
        {loading ? (
          <p className="audit-loading">Loading...</p>
        ) : entries.length === 0 ? (
          <p className="audit-empty">No audit log entries found.</p>
        ) : (
          <>
            <div className="audit-table">
              <div className="audit-table-header">
                <span>Timestamp</span>
                <span>Actor</span>
                <span>Action</span>
                <span>Entity</span>
                <span>IP</span>
                <span></span>
              </div>
              {entries.map(entry => (
                <React.Fragment key={entry.id}>
                  <div className="audit-table-row" onClick={() => entry.details && toggleRow(entry.id)}>
                    <span className="audit-timestamp">{formatDate(entry.created_at)}</span>
                    <span className="audit-actor">{entry.user_name || 'System'}</span>
                    <span className="audit-action">{entry.action}</span>
                    <span className="audit-entity">
                      {entry.entity_type}
                      {entry.entity_id && <span className="audit-entity-id">{entry.entity_id.substring(0, 8)}...</span>}
                    </span>
                    <span className="audit-ip">{entry.ip_address || '-'}</span>
                    <span className="audit-expand">
                      {entry.details && (
                        expandedRows.has(entry.id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </span>
                  </div>
                  {expandedRows.has(entry.id) && entry.details && (
                    <div className="audit-details-row">
                      <pre className="audit-details-json">
                        {JSON.stringify(entry.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="audit-pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft size={14} /> Previous
                </button>
                <span className="pagination-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AuditLog;
